import React, { useState, useEffect, useCallback } from 'react';
import { 
  FaMapMarkerAlt, 
  FaPhoneAlt, 
  FaEnvelope, 
  FaClock, 
  FaFacebook, 
  FaInstagram, 
  FaTwitter, 
  FaLinkedin,
  FaPaperPlane,
  FaCheckCircle,
  FaWhatsapp,
  FaDirections,
  FaHeadset,
  FaYoutube,
  FaTiktok,
  FaPinterest,
  FaTelegram,
  FaSnapchat,
  FaReddit,
  FaGlobe
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { database } from '../firebase';
import { ref as dbRef, onValue } from 'firebase/database';
import emailjs from '@emailjs/browser';


const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [contactData, setContactData] = useState({
    location: '',
    emergencyContact: '',
    phoneNumber: '',
    emailSupport: '',
    generalEmail: '',
    whatsappNumber: '',
    workingHours: '',
    mapEmbedUrl: '',
    socialLinks: []
  });

  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Fetch contact data from Firebase
  useEffect(() => {
    const contactRef = dbRef(database, 'Dr/contact');
    const unsubscribe = onValue(contactRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setContactData({
          location: data.location || '',
          emergencyContact: data.emergencyContact || '',
          phoneNumber: data.phoneNumber || '',
          emailSupport: data.emailSupport || '',
          generalEmail: data.generalEmail || '',
          whatsappNumber: data.whatsappNumber || '',
          workingHours: data.workingHours || '',
          mapEmbedUrl: data.mapEmbedUrl || '',
          socialLinks: data.socialLinks || []
        });
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Generate Google Maps embed URL from address
  const generateMapEmbedUrl = (address) => {
    if (!address || address.trim() === '') return '';
    const encodedAddress = encodeURIComponent(address);
    return `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodedAddress}`;
  };

  // Get current map URL
  const getCurrentMapUrl = () => {
    if (contactData.mapEmbedUrl && contactData.mapEmbedUrl.trim() !== '') {
      return contactData.mapEmbedUrl;
    }
    return generateMapEmbedUrl(contactData.location);
  };

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.4, ease: "easeOut" }
    }
  };

  // Contact information cards
  const contactInfo = [
    {
      id: 1,
      title: "Our Location",
      description: contactData.location,
      icon: <FaMapMarkerAlt />,
      color: "from-blue-500 to-cyan-500",
      action: "Get Directions",
      actionHandler: () => {
        const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contactData.location)}`;
        window.open(mapsUrl, '_blank');
      }
    },
    {
      id: 2,
      title: "Emergency Contact",
      description: `24/7 Emergency: ${contactData.emergencyContact}\nGeneral: ${contactData.phoneNumber}`,
      icon: <FaPhoneAlt />,
      color: "from-red-500 to-pink-500",
      action: "Call Now",
      actionHandler: () => {
        window.open(`tel:${contactData.emergencyContact.replace(/\D/g, '')}`, '_blank');
      },
      emergency: true
    },
    {
      id: 3,
      title: "Email Support",
      description: `Support: ${contactData.emailSupport}\nGeneral: ${contactData.generalEmail}`,
      icon: <FaEnvelope />,
      color: "from-green-500 to-emerald-500",
      action: "Send Email",
      actionHandler: () => {
        window.location.href = `mailto:${contactData.emailSupport}`;
      }
    },
    {
      id: 4,
      title: "Working Hours",
      description: contactData.workingHours,
      icon: <FaClock />,
      color: "from-purple-500 to-violet-500",
      action: "WhatsApp Us",
      actionHandler: () => {
        window.open(`https://wa.me/${contactData.whatsappNumber.replace(/\D/g, '')}`, '_blank');
      }
    }
  ];

  // Get social icon
  const getSocialIcon = (platform) => {
    const iconMap = {
      'Facebook': <FaFacebook />,
      'Instagram': <FaInstagram />,
      'Twitter': <FaTwitter />,
      'LinkedIn': <FaLinkedin />,
      'WhatsApp': <FaWhatsapp />,
      'YouTube': <FaYoutube />,
      'TikTok': <FaTiktok />,
      'Pinterest': <FaPinterest />,
      'Telegram': <FaTelegram />,
      'Snapchat': <FaSnapchat />,
      'Reddit': <FaReddit />
    };
    return iconMap[platform] || <FaGlobe />;
  };

  // Get social color
  const getSocialColor = (platform) => {
    const colorMap = {
      'Facebook': 'from-blue-600 to-blue-700',
      'Instagram': 'from-pink-600 to-rose-600',
      'Twitter': 'from-blue-400 to-cyan-400',
      'LinkedIn': 'from-blue-800 to-indigo-800',
      'WhatsApp': 'from-green-500 to-emerald-500',
      'YouTube': 'from-red-600 to-red-700',
      'TikTok': 'from-gray-900 to-black',
      'Pinterest': 'from-red-600 to-red-700',
      'Telegram': 'from-blue-500 to-blue-600',
      'Snapchat': 'from-yellow-400 to-yellow-500',
      'Reddit': 'from-orange-600 to-orange-700'
    };
    return colorMap[platform] || 'from-gray-600 to-gray-700';
  };

  const validateForm = () => {
    const errors = {};
    
    // Name validation - only letters
    if (!formData.name.trim()) {
      errors.name = 'Full name is required';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.name)) {
      errors.name = 'Name should contain only letters';
    } else if (formData.name.length < 3) {
      errors.name = 'Name must be at least 3 characters';
    }
    
    // Email validation
    if (!formData.email.trim()) {
      errors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Phone validation - only numbers, exactly 10 digits
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone)) {
      errors.phone = 'Phone number must be exactly 10 digits';
    }
    
    // Subject validation
    if (!formData.subject.trim()) {
      errors.subject = 'Subject is required';
    } else if (formData.subject.length < 10) {
      errors.subject = 'Subject must be at least 10 characters';
    }
    
    // Message validation
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    } else if (formData.message.length < 20) {
      errors.message = 'Message must be at least 20 characters';
    }
    
    return errors;
  };

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    
    // Clear previous error for this field
    setFormErrors(prev => ({
      ...prev,
      [name]: ''
    }));
    
    // Apply validation based on field type
    let validatedValue = value;
    
    if (name === 'name') {
      // Only allow letters and spaces
      validatedValue = value.replace(/[^a-zA-Z\s]/g, '');
    } else if (name === 'phone') {
      // Only allow numbers, max 10 digits
      validatedValue = value.replace(/\D/g, '').slice(0, 10);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: validatedValue
    }));
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();

    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);

    const templateParams = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      subject: formData.subject,
      message: formData.message,
    };

    // 1️⃣ Send email to ADMIN
    emailjs
      .send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_ADMIN_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      )

      // 2️⃣ Auto-reply to USER
      .then(() => {
        return emailjs.send(
          process.env.REACT_APP_EMAILJS_SERVICE_ID,
          process.env.REACT_APP_EMAILJS_AUTOREPLY_TEMPLATE_ID,
          templateParams,
          process.env.REACT_APP_EMAILJS_PUBLIC_KEY
        );
      })

      // ✅ Success
      .then(() => {
        setIsSubmitting(false);
        setShowSuccess(true);
        setFormErrors({}); // Clear all errors

        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });

        setTimeout(() => setShowSuccess(false), 3000);
      })

      // ❌ Error
      .catch((error) => {
        setIsSubmitting(false);
        console.error('EmailJS Error:', error);
        alert('❌ Message could not be sent. Please try again.');
      });
  }, [formData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="animate-pulse">
          <div className="text-xl text-gray-600">Loading contact information...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Success Toast */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="fixed top-4 right-4 z-50 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-3 rounded-xl shadow-2xl flex items-center"
          >
            <FaCheckCircle className="mr-3 text-xl" />
            <div>
              <p className="font-bold">Message Sent Successfully!</p>
              <p className="text-sm opacity-90">We'll contact you within 24 hours</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Section */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative min-h-[50vh] lg:min-h-[60vh] overflow-hidden bg-gradient-to-r from-teal-600 via-emerald-500 to-teal-400"
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-4 h-4 bg-white/20 rounded-full"
              initial={{ y: -100, x: Math.random() * 100 }}
              animate={{ 
                y: 800,
                x: Math.random() * 100 - 50,
              }}
              transition={{
                duration: Math.random() * 10 + 10,
                repeat: Infinity,
                delay: Math.random() * 5
              }}
              style={{
                left: `${Math.random() * 100}%`
              }}
            />
          ))}
        </div>

        <div className="relative h-full flex items-center py-8 lg:py-12 px-4 lg:px-8">
          <div className="max-w-7xl mx-auto w-full">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center lg:text-left"
            >
              <div className="inline-flex items-center mb-6 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                <div className="mr-3">
                  <FaHeadset className="text-white text-xl" />
                </div>
                <span className="font-semibold text-white">24/7 Healthcare Support</span>
              </div>

              <h1 className="text-3xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                Get in <span className="bg-gradient-to-r from-teal-400 to-emerald-400 bg-clip-text text-transparent">Touch</span>
              </h1>
              
              <p className="text-xl lg:text-2xl text-white/90 mb-8 max-w-3xl mx-auto lg:mx-0">
                Your health is our priority. Contact us anytime for support, inquiries, or emergency assistance.
              </p>

              {/* Quick Action Buttons */}
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => window.open(`tel:${contactData.emergencyContact.replace(/\D/g, '')}`, '_blank')}
                  className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-6 py-3 rounded-xl font-bold hover:shadow-2xl transition-all shadow-lg flex items-center"
                >
                  <FaPhoneAlt className="mr-3" />
                  Emergency Call
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => window.open(`https://wa.me/${contactData.whatsappNumber.replace(/\D/g, '')}`, '_blank')}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-xl font-bold hover:shadow-2xl transition-all shadow-lg flex items-center"
                >
                  <FaWhatsapp className="mr-3" />
                  WhatsApp Chat
                </motion.button>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Main Content */}
      <div className="py-8 lg:py-12 px-4 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Contact Information Cards */}
          <motion.section
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="mb-12 lg:mb-16"
          >
            <div className="text-center mb-8 lg:mb-12">
              <h2 className="text-2xl lg:text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
                How to Reach Us
              </h2>
              <div className="w-20 lg:w-24 h-1 bg-gradient-to-r from-blue-500 to-teal-500 mx-auto mb-4 rounded-full"></div>
              <p className="text-gray-600 max-w-3xl mx-auto">
                Multiple ways to connect with our healthcare team
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              {contactInfo.map((info) => (
                <motion.div
                  key={info.id}
                  variants={scaleIn}
                  whileHover={{ 
                    scale: 1.05, 
                    y: -5,
                    transition: { duration: 0.3 }
                  }}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-200"
                >
                  <div className="p-6">
                    <div className={`bg-gradient-to-r ${info.color} w-14 h-14 rounded-2xl flex items-center justify-center mb-6 mx-auto`}>
                      <div className="text-white text-2xl">
                        {info.icon}
                      </div>
                    </div>

                    <h3 className="font-bold text-gray-800 text-lg lg:text-xl mb-3 text-center">
                      {info.title}
                    </h3>
                    
                    <p className="text-gray-600 text-sm lg:text-base mb-6 text-center whitespace-pre-line min-h-[80px]">
                      {info.description || 'Not available'}
                    </p>

                    <div className="space-y-3">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={info.actionHandler}
                        className={`w-full bg-gradient-to-r ${info.color} text-white px-4 py-3 rounded-xl hover:shadow-lg transition-all font-medium`}
                      >
                        {info.action}
                      </motion.button>

                      {info.emergency && (
                        <div className="bg-gradient-to-r from-red-50 to-pink-50 p-3 rounded-lg">
                          <div className="flex items-center justify-center">
                            <FaPhoneAlt className="text-red-500 mr-2" />
                            <span className="text-red-600 font-bold">24/7 Available</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.section>

          {/* Contact Form & Map */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 mb-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 lg:p-8"
            >
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-2">Send Us a Message</h3>
                <p className="text-gray-600">We respond within 24 hours</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all ${
                        formErrors.name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter your name"
                    />
                    {formErrors.name && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all ${
                        formErrors.email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="your.email@example.com"
                    />
                    {formErrors.email && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.email}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all ${
                        formErrors.phone ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="+91 98765 43210"
                    />
                    {formErrors.phone && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.phone}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Subject *</label>
                    <input
                      type="text"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all ${
                        formErrors.subject ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter your subject"
                    />
                    {formErrors.subject && (
                      <p className="mt-1 text-sm text-red-600">{formErrors.subject}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">Your Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows="4"
                    className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all resize-none ${
                      formErrors.message ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Please describe your query in detail..."
                  ></textarea>
                  {formErrors.message && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.message}</p>
                  )}
                </div>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-6 py-3 rounded-xl font-bold hover:shadow-xl transition-all flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane className="mr-3" />
                      Send Message
                    </>
                  )}
                </motion.button>
              </form>
            </motion.div>

            {/* Map Preview */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200"
            >
              <div className="p-4 bg-gradient-to-r from-teal-50 to-cyan-50 border-b flex items-center justify-between">
                <h3 className="font-bold text-gray-800 flex items-center">
                  <FaMapMarkerAlt className="mr-2 text-teal-600" />
                  Our Location
                </h3>
                {contactData.location && (
                  <button
                    onClick={() => {
                      const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contactData.location)}`;
                      window.open(mapsUrl, '_blank');
                    }}
                    className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <FaDirections /> Open
                  </button>
                )}
              </div>
              
              {getCurrentMapUrl() ? (
                <iframe
                  src={getCurrentMapUrl()}
                  className="w-full h-96 border-0"
                  allowFullScreen=""
                  loading="lazy"
                  title="Location Map"
                ></iframe>
              ) : (
                <div className="h-96 bg-gray-200 flex flex-col items-center justify-center text-gray-500 p-6 text-center">
                  <FaMapMarkerAlt className="text-4xl mb-3 text-gray-400" />
                  <p className="font-medium">No location address available</p>
                </div>
              )}

              <div className="p-6">
                <h4 className="font-bold text-gray-800 mb-4">Address</h4>
                <p className="text-gray-600 whitespace-pre-line mb-4">
                  {contactData.location || 'Address not available'}
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contactData.location)}`;
                    window.open(mapsUrl, '_blank');
                  }}
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-4 py-3 rounded-xl hover:shadow-lg transition-all flex items-center justify-center font-medium"
                >
                  <FaDirections className="mr-3" />
                  Get Directions
                </motion.button>
              </div>
            </motion.div>
          </div>

          {/* Social Media - Only show if links exist */}
          {contactData.socialLinks && contactData.socialLinks.length > 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-6 lg:p-8 text-white"
            >
              <h3 className="text-2xl font-bold mb-6">Connect With Us</h3>
              <p className="text-gray-300 mb-8">
                Follow us on social media for health tips, updates, and wellness advice
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {contactData.socialLinks.map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.1 }}
                    className={`bg-gradient-to-r ${getSocialColor(social.platform)} rounded-xl p-4 text-center hover:shadow-2xl transition-all`}
                  >
                    <div className="text-2xl mb-2">{getSocialIcon(social.platform)}</div>
                    <div className="text-sm font-medium">{social.platform}</div>
                  </motion.a>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactUs;